
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  Link,
  useNavigate,
  useParams
} from 'react-router-dom';

import AnimalMap from '../components/AnimalMap';
import { listsApi } from '../utils/listsApi';
import './Lists.css';

const API_URL = (
  process.env.REACT_APP_API_URL || ''
).replace(/\/+$/, '');

function getImageUrl(path) {
  if (!path) return null;

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${API_URL}/${path.replace(/^\/+/, '')}`;
}

function ListDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [list, setList] = useState(null);
  const [intersection, setIntersection] = useState(null);

  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');
  const [mapError, setMapError] = useState('');
  const [message, setMessage] = useState('');

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [draft, setDraft] = useState('');

  // Start editing the name or description
  function startEditing(field) {
    if (saving) return;

    setEditingField(field);
    setDraft(list[field] || '');
    setError('');
    setMessage('');
  }

  // Cancel editing
  function cancelEditing() {
    setEditingField(null);
    setDraft('');
    setError('');
  }

  // Save name or description
  async function handleSave(e) {
    e.preventDefault();

    if (!editingField || saving) return;

    const field = editingField;

    const value = field === 'name'
      ? draft.trim()
      : draft;

    if (field === 'name' && !value) {
      setError('List name cannot be empty.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const updated = await listsApi(`/lists/${id}`, {
        method: 'PATCH',
        body: {
          [field]: value
        }
      });

      setList(previous => ({
        ...previous,
        ...updated
      }));

      setEditingField(null);
      setDraft('');
      setMessage(
        field === 'name'
          ? 'List name updated.'
          : 'Description updated.'
      );

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Load intersection
  const loadIntersection = useCallback(async () => {
    setMapLoading(true);
    setMapError('');

    try {
      const geometry = await listsApi(
        `/lists/${id}/intersection`
      );

      if (
        geometry !== null &&
        !['Polygon', 'MultiPolygon'].includes(geometry?.type)
      ) {
        throw new Error('Invalid geographic data received.');
      }

      setIntersection(geometry);

    } catch (err) {
      setIntersection(null);
      setMapError(err.message);
    } finally {
      setMapLoading(false);
    }
  }, [id]);

  // Load list
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }

    let active = true;

    async function loadList() {
      setLoading(true);

      try {
        const data = await listsApi(`/lists/${id}`);

        if (!active) return;

        setList(data);
        setError('');
        setEditingField(null);

      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadList();
    loadIntersection();

    return () => {
      active = false;
    };
  }, [id, navigate, loadIntersection]);

  // Remove animal
  async function handleRemove(animalId) {
    setError('');
    setMessage('');

    try {
      await listsApi(
        `/lists/${id}/animals/${animalId}`,
        { method: 'DELETE' }
      );

      setList(previous => ({
        ...previous,
        animals: previous.animals.filter(
          animal => animal.id !== animalId
        )
      }));

      await loadIntersection();

    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) {
    return (
      <main className="list-detail-page">
        <p>Loading list...</p>
      </main>
    );
  }

  if (!list) {
    return (
      <main className="list-detail-page">
        <p className="lists-error">
          {error || 'List not found.'}
        </p>
        <Link to="/lists">← Back to Lists</Link>
      </main>
    );
  }

  return (
    <main className="list-detail-page">

      <Link to="/lists" className="list-back-link">
        ← Back to Lists
      </Link>

      {/* NAME */}
      {editingField === 'name' ? (
        <form
          className="list-inline-edit"
          onSubmit={handleSave}
        >
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            maxLength={18}
            required
            autoFocus
            aria-label="List name"
          />

          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>

          <button
            type="button"
            className="list-cancel-btn"
            onClick={cancelEditing}
            disabled={saving}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="list-title-row">
          <h1>{list.name}</h1>

          <button
            type="button"
            className="list-pencil-btn"
            onClick={() => startEditing('name')}
            aria-label="Edit list name"
            title="Edit name"
          >
            ✎
          </button>
        </div>
      )}

      {/* DESCRIPTION */}
      {editingField === 'description' ? (
        <form
          className="list-description-edit"
          onSubmit={handleSave}
        >
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            maxLength={200}
            rows={3}
            autoFocus
            aria-label="List description"
          />

          <div className="list-edit-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>

            <button
              type="button"
              className="list-cancel-btn"
              onClick={cancelEditing}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="list-description-row">
          <p>
            {list.description || 'No description yet.'}
          </p>

          <button
            type="button"
            className="list-pencil-btn"
            onClick={() => startEditing('description')}
            aria-label="Edit list description"
            title="Edit description"
          >
            ✎
          </button>
        </div>
      )}

      {error && (
        <p className="lists-error" role="alert">
          {error}
        </p>
      )}

      {message && (
        <p className="lists-success" role="status">
          {message}
        </p>
      )}

      {/* ANIMALS */}
      <section className="list-animals-section">
        <h2>Animals ({list.animals.length})</h2>

        {list.animals.length === 0 ? (
          <p>
            This list is empty. Add animals from
            their individual pages or search results.
          </p>
        ) : (
          <div className="list-animals-grid">
            {list.animals.map(animal => {
              const imageUrl = getImageUrl(
                animal.image_path
              );

              return (
                <article
                  className="list-animal-card"
                  key={animal.id}
                >
                  <Link to={`/animal/${animal.id}`}>

                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={animal.name}
                        loading="lazy"
                      />
                    ) : (
                      <div className="list-image-placeholder">
                        No image available
                      </div>
                    )}

                    <h3>{animal.name}</h3>

                    <p>
                      <em>{animal.sci_name}</em>
                    </p>
                  </Link>

                  <button
                    type="button"
                    className="list-remove-btn"
                    onClick={() => handleRemove(animal.id)}
                  >
                    Remove from List
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* GEOGRAPHIC INTERSECTION */}
      <section className="list-map-section">
        <h2>Geographic Intersection</h2>

        {mapLoading ? (
          <p>Calculating geographic intersection...</p>
        ) : mapError ? (
          <p className="lists-error">{mapError}</p>
        ) : intersection ? (
          <div className="list-map-container">
            <AnimalMap geojson={intersection} />
          </div>
        ) : (
          <p>
            No overlapping geographic area found
            between the animals in this list.
          </p>
        )}
      </section>

    </main>
  );
}

export default ListDetailPage;
